import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { SurveyCard } from '@/pages/Mail/components/SurveyCard';
import { SurveyFillView } from '@/pages/Mail/components/SurveyFillView';
import { SurveyResultsView } from '@/pages/Mail/components/SurveyResultsView';
import {
  SurveyStateAction,
  SurveyStateBody,
} from '@/pages/Mail/components/SurveyStateBody';
import { useSurvey } from '@/pages/Mail/hooks/useSurvey';
import { useSurveyResults } from '@/pages/Mail/hooks/useSurveyResults';
import { useStartSurvey } from '@/pages/Mail/hooks/useStartSurvey';
import { SurveyDetailState } from '@/types/components';
import type { Survey } from '@/types/survey';

const TITLE_SWAP_PROGRESS = 0.5;

interface Props {
  survey: Survey;
  isMobile: boolean;
  isFillRequested: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  onError: () => void;
}

export const SurveyDetailModal: React.FC<Props> = ({
  survey,
  isMobile,
  isFillRequested,
  onClose,
  onSubmitted,
  onError,
}) => {
  const { t } = useTranslation();
  const title = t('mail.surveys.detail.title');
  const [isFilling, setIsFilling] = useState(isFillRequested);
  const { progress, handleScroll } = useScrollFadeProgress();

  const { data: surveyDetail, isLoading: isDetailLoading } = useSurvey(
    survey.id,
  );

  const { detailState, questionCount, questionIds } = surveyDetail ?? survey;
  const areResultsShown = detailState === SurveyDetailState.Results;
  const { data: results, isLoading: isResultsLoading } = useSurveyResults(
    survey.id,
    areResultsShown,
  );
  const { startSurvey, isStarting } = useStartSurvey();
  const isSurveyNew = detailState === SurveyDetailState.New;
  const isLoading = isDetailLoading || (areResultsShown && isResultsLoading);
  const isFillVisible = isFilling && questionIds.length > 0;
  const isContentVisible = !isFillVisible && !isLoading;
  const isTitleInHeader = progress > TITLE_SWAP_PROGRESS;
  const isHeaderVisible = !isMobile || !isFillVisible;
  const isActionVisible = !areResultsShown;

  const fillSurvey = () => {
    if (!isSurveyNew) {
      setIsFilling(true);
      return;
    }

    startSurvey(survey.id, { onSuccess: () => setIsFilling(true) });
  };

  const content = (
    <div className="flex min-h-full flex-col px-4 pt-2 md:min-h-0 md:px-5 md:pt-0.5 md:pb-5">
      <div className="flex flex-1 flex-col gap-6">
        <h2
          className={`text-title-36 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-0' : 'opacity-100'}`}
        >
          {title}
        </h2>

        <SurveyCard survey={survey} isMobile={isMobile} isSummary />

        {areResultsShown && results && <SurveyResultsView results={results} />}

        {!areResultsShown && (
          <SurveyStateBody state={detailState} questionCount={questionCount} />
        )}

        {isActionVisible && !isMobile && (
          <SurveyStateAction
            isMobile={isMobile}
            isStarting={isStarting}
            state={detailState}
            onFill={fillSurvey}
          />
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      label={title}
      className="md:h-[788px] md:max-h-[calc(100svh-80px)] md:w-[min(853px,calc(100vw-40px))]"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {isHeaderVisible && (
          <header className="relative flex shrink-0 items-center gap-3 px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
            <button
              type="button"
              aria-label={t('global.back')}
              onClick={onClose}
              className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
            >
              <ArrowLeft />
            </button>

            <h2
              className={`text-callout-16 pointer-events-none absolute left-1/2 -translate-x-1/2 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-100' : 'opacity-0'}`}
            >
              {title}
            </h2>

            <h2 className="text-headline-20 hidden flex-1 text-foreground md:block">
              {title}
            </h2>

            <button
              type="button"
              aria-label={t('global.close')}
              onClick={onClose}
              className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
            >
              <Close />
            </button>
          </header>
        )}

        {isFillVisible && (
          <SurveyFillView
            surveyId={survey.id}
            questionIds={questionIds}
            isMobile={isMobile}
            onExit={() => setIsFilling(false)}
            onSubmitted={onSubmitted}
            onError={onError}
          />
        )}

        {!isFillVisible && isLoading && (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <PageSpinner className="size-[22px] text-subtle-foreground" />
          </div>
        )}

        {isContentVisible && isMobile && (
          <>
            <div
              data-modal-scroll
              onScroll={handleScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
            >
              {content}
            </div>

            {isActionVisible && (
              <SurveyStateAction
                isMobile={isMobile}
                isStarting={isStarting}
                state={detailState}
                onFill={fillSurvey}
              />
            )}
          </>
        )}

        {isContentVisible && !isMobile && (
          <AutoHideScrollArea
            className="flex min-h-0 flex-1 flex-col"
            viewportClassName="[&>div]:h-full"
            onScroll={handleScroll}
          >
            {content}
          </AutoHideScrollArea>
        )}
      </div>
    </Modal>
  );
};
